import React from 'react'
import { Box, Checkbox, FormControlLabel, Typography, useTheme } from '@mui/material'
import { black, blue, grey, shade } from '../../../colors'

import ReportsChip from './ReportsChip'
import { type ICalculation } from '../../../models/Report'
import TermsTabs from './TermsTabs'
import { ReportsActionType, useReportsContext } from '../ReportsContext'
import CustomTooltip from '../../../components/CustomTooltip'
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft'
import AlignVerticalBottomIcon from '@mui/icons-material/AlignVerticalBottom'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import { getReportTitleDisplayItems, getResponseFilterLabelPrefix } from './reportTitles'
import { filterVisibleCalculations, hasDisplayValue, isCommentsExportAvailable } from './helper'
import Dropdown from '../../../components/Dropdown'
import GenericButton from '../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import { type ReportChartType, type VisibleKeyStats } from '../types'
import { keyStatSwitches, reportChartTypeOptions, responseFilterActiveColor, responseFilterBaselineColor, responseFilterBaselineTextColor } from './constants'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { type ReportResponseFilterQuestionOption } from './helper'
import { formatDateByUserTimeZone } from '../../../helpers/formatDateByUserTimeZone'
import { useUserRole } from '../../../hooks/useUserRole'
import { ROLES } from '../../../consts/roles'

const AVERAGE_LABEL = 'Average'
const responseFilterHeaderBadgeTextColor = grey[0]
const LAST_UPDATED_DATE_FORMAT = "MMM d, yyyy 'at' h:mm a"

interface TopLevelSurveyResultProps {
  calculations: ICalculation[] | null | undefined
  comparisonCalculations?: ICalculation[] | null
  titles?: string[]
  onToggleExpanded: () => void
  isExpanded: boolean
  disableControls?: boolean
  onRemoveResponseFilterAnswer?: (answer: string) => void | Promise<void>
  responseFilterAnswerOrder?: string[]
  responseFilterQuestionOptions?: ReportResponseFilterQuestionOption[]
  responseFilterWarningMessage?: string | null
  onOpenCommentsExport?: () => void
  isSectionLevel?: boolean
  // CR-271 Behavior Change #9: when this section report's underlying data was last
  // computed. Account-Owner-only display (see the "Last updated" render below) even
  // though the Backend sends this field to every role.
  modifiedOn?: string | null
}

export const TopLevelSurveyResult: React.FC<TopLevelSurveyResultProps> = ({
  calculations,
  comparisonCalculations = null,
  titles,
  isExpanded,
  onToggleExpanded,
  disableControls = false,
  onRemoveResponseFilterAnswer,
  responseFilterAnswerOrder = [],
  responseFilterQuestionOptions = [],
  responseFilterWarningMessage = null,
  onOpenCommentsExport,
  isSectionLevel = false,
  modifiedOn = null
}) => {
  const theme = useTheme()
  const { selectedTrend, chartOrientation, dispatch, surveyVisibleKeyStats, globalChartType, displaySettings } = useReportsContext()
  const { activeRole } = useUserRole()
  const isAccountOwner = activeRole?.roleName === ROLES.ACCOUNT_OWNER
  // CR-271: Account-Owner-only, and only for the single-section report this field
  // actually describes — division/aggregate reports don't carry this timestamp.
  const showLastUpdated = isAccountOwner && isSectionLevel
  const lastUpdatedAt = modifiedOn ? (formatDateByUserTimeZone(modifiedOn, LAST_UPDATED_DATE_FORMAT) || '--') : '--'

  const isCurrentTermTab = selectedTrend === 1
  const showStats = selectedTrend <= 2
  const showChartTypeDropdown = isCurrentTermTab && (titles?.length ?? 0) > 1
  const noDataTooltip = 'Controls are disabled because no chart data is available.'
  const commentsExportDisabledTooltip = 'Free response question exports are only available for basic reports and reports scoped by attributes.'
  const verticalOrientationTooltip = disableControls
    ? noDataTooltip
    : 'Switch survey response chart orientation to vertical.'
  const horizontalOrientationTooltip = disableControls
    ? noDataTooltip
    : 'Switch survey response chart orientation to horizontal.'
  const chartOrientationButtonSx = (isSelected: boolean, isLeading: boolean): Record<string, any> => ({
    minWidth: 40,
    width: 40,
    px: 0,
    ml: isLeading ? 0 : '-1px',
    borderRadius: isLeading
      ? `${theme.spacing(2)} 0 0 ${theme.spacing(2)}`
      : `0 ${theme.spacing(2)} ${theme.spacing(2)} 0`,
    '&:hover': {
      backgroundColor: grey[100]
    },
    '&[aria-pressed="true"]': {
      backgroundColor: grey[100],
      color: grey[950],
      '&:hover': {
        backgroundColor: grey[100]
      }
    },
    ...(isSelected ? { color: grey[950] } : {})
  })
  const expandAllTooltip = isExpanded
    ? 'Collapse all survey sections in the results.'
    : 'Expand all survey sections in the results.'
  const detailTooltip = disableControls
    ? noDataTooltip
    : expandAllTooltip
  const detailAriaLabel = isExpanded
    ? 'Collapse all survey sections in the results'
    : 'Expand all survey sections in the results'
  const canExportComments = isCommentsExportAvailable(displaySettings, selectedTrend) && !disableControls
  const commentsExportTooltip = canExportComments ? 'Download comments' : commentsExportDisabledTooltip
  const displayTitles = getReportTitleDisplayItems(titles ?? [])
  const responseFilterAnswers = displaySettings.responseFilter?.answers ?? []
  const responseFilterLabelPrefix = getResponseFilterLabelPrefix(displaySettings, responseFilterQuestionOptions)
  const responseFilterAnswerOrderMap = new Map(responseFilterAnswerOrder.map((answer, index) => [answer, index]))
  const orderedResponseFilterAnswers = [...responseFilterAnswers].sort((leftAnswer, rightAnswer) => {
    const leftOrder = responseFilterAnswerOrderMap.get(leftAnswer) ?? Number.MAX_SAFE_INTEGER
    const rightOrder = responseFilterAnswerOrderMap.get(rightAnswer) ?? Number.MAX_SAFE_INTEGER

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder
    }

    return leftAnswer.localeCompare(rightAnswer)
  })
  const baseDisplayTitle = displayTitles[0]
  const shouldRenderResponseFilterBadges = orderedResponseFilterAnswers.length > 0 && Boolean(responseFilterLabelPrefix)
  const shouldRenderDerivedTitleChips = !shouldRenderResponseFilterBadges && displayTitles.length > 1
  const visibleCalculations = filterVisibleCalculations(calculations, surveyVisibleKeyStats)
  const visibleComparisonCalculations = filterVisibleCalculations(comparisonCalculations, surveyVisibleKeyStats)
  const isResponseFilterCalculationComparison = visibleComparisonCalculations.length > 0
  const selectedChartTypeLabel = reportChartTypeOptions.find(({ value }) => value === globalChartType)?.label ?? 'Distribution'
  const anyKeyStatsSelected = keyStatSwitches.some(({ name }) => surveyVisibleKeyStats[name])
  const allVisibleKeyStats = keyStatSwitches.reduce<VisibleKeyStats>((acc, { name }) => {
    acc[name] = true
    return acc
  }, {
    average: false,
    median: false,
    mode: false,
    top2: false,
    bottom2: false,
    stdev: false
  })
  const noVisibleKeyStats = {
    average: false,
    median: false,
    mode: false,
    top2: false,
    bottom2: false,
    stdev: false
  }
  const comparisonCalculationsByName = new Map(visibleComparisonCalculations.map((calculation) => [calculation.name, calculation]))
  const combinedVisibleCalculations = visibleCalculations.reduce<ICalculation[]>((acc, calculation) => {
    acc.push(calculation)
    return acc
  }, [])

  visibleComparisonCalculations.forEach((calculation) => {
    if (!combinedVisibleCalculations.some((currentCalculation) => currentCalculation.name === calculation.name)) {
      combinedVisibleCalculations.push(calculation)
    }
  })

  const formatCalculationChipLabel = (name: string, value: unknown): string | number => {
    if (!hasDisplayValue(value)) {
      return 'N/A'
    }

    return name === AVERAGE_LABEL ? String(value) : `${value}%`
  }

  const handleGlobalChartTypeChange = (chartType: ReportChartType): void => {
    dispatch({ type: ReportsActionType.SET_GLOBAL_CHART_TYPE, payload: chartType })
  }

  const keyStatsMenuContent = (
    <Box display="flex" flexDirection="column" gap={1} minWidth={220}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="body2" fontWeight={600} color={black[100]}>
          Show Key Stats
        </Typography>
        <Typography
          component="button"
          type="button"
          onClick={() => {
            dispatch({
              type: ReportsActionType.SET_SURVEY_VISIBLE_KEY_STATS,
              payload: anyKeyStatsSelected ? noVisibleKeyStats : allVisibleKeyStats
            })
          }}
          sx={{
            border: 0,
            background: 'transparent',
            p: 0,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: blue[500],
            cursor: 'pointer'
          }}
        >
          {anyKeyStatsSelected ? 'Deselect All' : 'Select All'}
        </Typography>
      </Box>
      {keyStatSwitches.map(({ label, name }) => (
        <FormControlLabel
          key={name}
          control={
            <Checkbox
              checked={surveyVisibleKeyStats[name]}
              disabled={disableControls}
              onChange={(event) => {
                dispatch({
                  type: ReportsActionType.SET_SURVEY_VISIBLE_KEY_STATS,
                  payload: {
                    ...surveyVisibleKeyStats,
                    [name]: event.target.checked
                  }
                })
              }}
            />
          }
          label={label}
          sx={{ m: 0 }}
        />
      ))}
    </Box>
  )
  // if (calculations?.length === 0 && (!titles || titles.length <= 1)) return null

  return (
    <Box
      sx={{
        borderRadius: theme.spacing(2),
        padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
        display: 'flex',
        gap: theme.spacing(5),
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <Box display={'flex'} flexDirection={'column'} gap={1} >
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} flexWrap={'wrap'} gap={1.5}>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography variant="h6" color={black[100]} fontSize="22px" fontWeight="600">Survey Results </Typography>
            {showLastUpdated && (
              <Typography variant="caption" color="text.secondary">
                Last updated: {lastUpdatedAt}
              </Typography>
            )}
            {shouldRenderResponseFilterBadges && orderedResponseFilterAnswers.map((answer: string) => {
              const fullTitle = `${responseFilterLabelPrefix}${answer}`
              const showWarningBadgeStyle = Boolean(responseFilterWarningMessage)

              return (
                <ReportsChip
                  key={fullTitle}
                  index={0}
                  label={fullTitle}
                  tooltipTitle={showWarningBadgeStyle ? responseFilterWarningMessage : fullTitle}
                  ariaLabel={fullTitle}
                  maxWidth={320}
                  icon={showWarningBadgeStyle ? <WarningAmberRoundedIcon sx={{ color: grey[250], opacity: 1 }} /> : undefined}
                  backgroundColor={showWarningBadgeStyle ? shade[650] : responseFilterActiveColor}
                  textColor={showWarningBadgeStyle ? grey[250] : responseFilterHeaderBadgeTextColor}
                  onDelete={onRemoveResponseFilterAnswer ? () => { void onRemoveResponseFilterAnswer(answer) } : undefined}
                />
              )
            })}
          </Box>
          <Box display={'flex'} alignItems={'center'} gap={1.5} flexWrap={'wrap'}>
            {isCurrentTermTab && (
              <>
                <Box display="inline-flex" role="group" aria-label="Survey response chart orientation">
                  <CustomTooltip title={verticalOrientationTooltip}>
                    <Box>
                      <GenericButton
                        buttonType={ButtonType.Secondary}
                        buttonSize={ButtonSize.Small}
                        disabled={disableControls}
                        icon={<AlignVerticalBottomIcon />}
                        aria-label="Vertical chart orientation"
                        aria-pressed={chartOrientation === 'vertical'}
                        onClick={() => {
                          if (chartOrientation !== 'vertical') {
                            dispatch({ type: ReportsActionType.SET_CHART_ORIENTATION, payload: 'vertical' })
                          }
                        }}
                        sx={chartOrientationButtonSx(chartOrientation === 'vertical', true)}
                      />
                    </Box>
                  </CustomTooltip>
                  <CustomTooltip title={horizontalOrientationTooltip}>
                    <Box>
                      <GenericButton
                        buttonType={ButtonType.Secondary}
                        buttonSize={ButtonSize.Small}
                        disabled={disableControls}
                        icon={<AlignHorizontalLeftIcon />}
                        aria-label="Horizontal chart orientation"
                        aria-pressed={chartOrientation === 'horizontal'}
                        onClick={() => {
                          if (chartOrientation !== 'horizontal') {
                            dispatch({ type: ReportsActionType.SET_CHART_ORIENTATION, payload: 'horizontal' })
                          }
                        }}
                        sx={chartOrientationButtonSx(chartOrientation === 'horizontal', false)}
                      />
                    </Box>
                  </CustomTooltip>
                </Box>
                {showChartTypeDropdown && (
                  <Dropdown
                    label={selectedChartTypeLabel}
                    disabled={disableControls}
                    buttonType={ButtonType.Secondary}
                    buttonSize={ButtonSize.Small}
                    menuItems={reportChartTypeOptions.map(({ label, value }) => ({
                      text: label,
                      action: () => { handleGlobalChartTypeChange(value) }
                    }))}
                    extraButtonProps={{
                      sx: {
                        px: 2.5,
                        minWidth: 140
                      }
                    }}
                    menuPaperStyles={{ minWidth: 180 }}
                  />
                )}
              </>
            )}
            {showStats && (
              <Dropdown
                label="Key Stats"
                disabled={disableControls}
                buttonType={ButtonType.Secondary}
                buttonSize={ButtonSize.Small}
                menuItems={keyStatsMenuContent}
                extraButtonProps={{
                  sx: {
                    px: 2.5
                  }
                }}
                menuPaperStyles={{ minWidth: 220 }}
              />
            )}
            <CustomTooltip title={detailTooltip}>
              <Box>
                <GenericButton
                  buttonType={ButtonType.Secondary}
                  buttonSize={ButtonSize.Small}
                  disabled={disableControls}
                  icon={isExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                  aria-label={detailAriaLabel}
                  aria-pressed={isExpanded}
                  onClick={onToggleExpanded}
                  sx={{
                    minWidth: 40,
                    width: 40,
                    px: 0,
                    '&[aria-pressed="true"]': {
                      backgroundColor: grey[100],
                      color: grey[950]
                    }
                  }}
                />
              </Box>
            </CustomTooltip>
            {!isSectionLevel && (
              <CustomTooltip title={commentsExportTooltip}>
                <Box>
                  <GenericButton
                    id="survey-results-comments-export-button"
                    buttonSize={ButtonSize.Small}
                    buttonType={ButtonType.Secondary}
                    title="Download comments"
                    disabled={!canExportComments}
                    onClick={onOpenCommentsExport}
                    icon={<CloudDownloadIcon />}
                    aria-label="Download comments"
                  />
                </Box>
              </CustomTooltip>
            )}
          </Box>
        </Box>
        <TermsTabs />
        {showStats && <Box display={'flex'} gap={2} flexWrap='wrap'>
          {shouldRenderDerivedTitleChips && baseDisplayTitle && (
            <ReportsChip
              index={0}
              label={baseDisplayTitle.displayTitle}
              tooltipTitle={baseDisplayTitle.fullTitle}
              ariaLabel={baseDisplayTitle.fullTitle}
              maxWidth={320}
            />
          )}
          {shouldRenderDerivedTitleChips && displayTitles.slice(1).map(({ fullTitle, displayTitle }, index) => (
            <ReportsChip
              key={fullTitle}
              index={index + 1}
              label={displayTitle}
              tooltipTitle={fullTitle}
              ariaLabel={fullTitle}
              maxWidth={320}
            />
          ))}
        </Box>}
      </Box>
      {
        combinedVisibleCalculations.length > 0 && showStats &&
        <Box display="flex" flexWrap={'wrap'} gap={4} >
          {combinedVisibleCalculations.map(({ name, values }) => {
            const label = name === AVERAGE_LABEL ? 'Overall Average' : name
            const comparisonValues = comparisonCalculationsByName.get(name)?.values ?? []
            const currentValue = values?.[0]
            const comparisonValue = comparisonValues?.[0]
            return (
              <Box
                key={name}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: theme.spacing(2.5)
                }}
              >
                <Typography fontWeight={600} color={black[100]} whiteSpace={'nowrap'}>{label}:</Typography>
                {isResponseFilterCalculationComparison
                  ? (
                    <React.Fragment>
                      <ReportsChip
                        index={0}
                        label={formatCalculationChipLabel(name, comparisonValue)}
                        backgroundColor={responseFilterBaselineColor}
                        textColor={responseFilterBaselineTextColor}
                      />
                      <ReportsChip
                        index={1}
                        label={formatCalculationChipLabel(name, currentValue)}
                        backgroundColor={responseFilterActiveColor}
                      />
                    </React.Fragment>
                    )
                  : values?.map((value, index) => {
                    const chipLabel = formatCalculationChipLabel(name, value)

                    return (
                      <ReportsChip
                        key={index}
                        index={index}
                        label={chipLabel}
                      />
                    )
                  })}
              </Box>
            )
          })}
        </Box>
      }
    </Box>
  )
}

export default TopLevelSurveyResult
